/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardMedia,
    Typography,
    Button,
    Chip,
    Skeleton,
    IconButton,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Hotel,
    Flight,
    DirectionsCar,
    Attractions,
    Security,
    ChevronRight,
} from '@mui/icons-material';
import { Ad, AdPage, AdCategory } from '../../data/mockAds';
import { useAds } from '../../contexts/AdContext';
import AdDrawer from './AdDrawer';

interface SidebarAdProps {
    page: AdPage;
    maxAds?: number;
    contextCategory?: AdCategory;
}

const categoryIcons: Record<AdCategory, React.ReactElement> = {
    hotel: <Hotel sx={{ fontSize: 16 }} />,
    flight: <Flight sx={{ fontSize: 16 }} />,
    car: <DirectionsCar sx={{ fontSize: 16 }} />,
    activity: <Attractions sx={{ fontSize: 16 }} />,
    insurance: <Security sx={{ fontSize: 16 }} />,
    general: <Attractions sx={{ fontSize: 16 }} />,
};

const categoryColors: Record<AdCategory, string> = {
    hotel: '#4A6FA5',
    flight: '#2196F3',
    car: '#607D8B',
    activity: '#2D6E6A',
    insurance: '#FF9800',
    general: '#757575',
};

const SidebarAd: React.FC<SidebarAdProps> = ({ page, maxAds = 3, contextCategory }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { getAdsForPage, trackImpression, trackClick, shouldShowAds, isDelayPassed } = useAds();

    const [ads, setAds] = useState<Ad[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedAd, setSelectedAd] = useState<Ad | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [impressionsTracked, setImpressionsTracked] = useState<Set<string>>(new Set());

    // Fetch ads
    useEffect(() => {
        if (!shouldShowAds(page)) {
            setLoading(false);
            return;
        }
        const fetchedAds = getAdsForPage(page, 'sidebar', contextCategory).slice(0, maxAds);
        if (contextCategory) {
            const sorted = [...fetchedAds].sort((a, b) => {
                if (a.category === contextCategory && b.category !== contextCategory) return -1;
                if (b.category === contextCategory && a.category !== contextCategory) return 1;
                return 0;
            });
            setAds(sorted);
        } else {
            setAds(fetchedAds);
        }
        setLoading(false);
    }, [page, maxAds, contextCategory, getAdsForPage, shouldShowAds, isDelayPassed]);

    // Track impressions
    useEffect(() => {
        if (!shouldShowAds(page)) return;
        ads.forEach(ad => {
            if (!impressionsTracked.has(ad.id)) {
                trackImpression(ad, page, 'sidebar');
                setImpressionsTracked(prev => new Set(prev).add(ad.id));
            }
        });
    }, [ads, page, trackImpression, impressionsTracked, shouldShowAds]);

    const handleAdClick = useCallback((ad: Ad) => {
        if (!shouldShowAds(page)) return;
        trackClick(ad, page, 'sidebar', 'click');
        setSelectedAd(ad);
        setDrawerOpen(true);
    }, [page, trackClick, shouldShowAds]);

    const handleDrawerClose = useCallback(() => {
        setDrawerOpen(false);
        setSelectedAd(null);
    }, []);

    // Don't render on mobile or if disabled
    if (isMobile || !shouldShowAds(page)) {
        return null;
    }

    if (loading) {
        return (
            <Box sx={{ position: 'sticky', top: 80, borderLeft: '1px solid #E0E0E0', pl: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Sponsored
                </Typography>
                {[1, 2].map(i => (
                    <Card key={i} sx={{ mb: 2, borderRadius: 2 }}>
                        <Skeleton variant="rectangular" height={120} />
                        <Box sx={{ p: 1.5 }}>
                            <Skeleton variant="text" width="80%" />
                            <Skeleton variant="text" width="60%" />
                        </Box>
                    </Card>
                ))}
            </Box>
        );
    }

    if (ads.length === 0) {
        return null;
    }

    return (
        <>
            <Box sx={{ position: 'sticky', top: 80, borderLeft: '1px solid #E0E0E0', pl: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Sponsored
                </Typography>
                {ads.map((ad, index) => (
                    <Card
                        key={ad.id}
                        onClick={() => handleAdClick(ad)}
                        sx={{
                            mb: 2,
                            borderRadius: 2,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            border: ad.tier === 'premium' ? '2px solid #FFD700' : '1px solid #E0E0E0',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            },
                        }}
                    >
                        {ad.image && (
                            <CardMedia
                                component="img"
                                height={index === 0 ? 140 : 100}
                                image={ad.image}
                                alt={ad.title}
                                sx={{ objectFit: 'cover' }}
                            />
                        )}
                        <Box sx={{ p: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <Chip
                                    icon={categoryIcons[ad.category]}
                                    label={ad.category.replace('_', ' ')}
                                    size="small"
                                    sx={{
                                        height: 20,
                                        fontSize: '0.65rem',
                                        bgcolor: `${categoryColors[ad.category]}15`,
                                        color: categoryColors[ad.category],
                                        textTransform: 'capitalize',
                                        '& .MuiChip-icon': { color: categoryColors[ad.category] },
                                    }}
                                />
                                {ad.tier === 'premium' && (
                                    <Chip label="Premium" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#FFD700', color: '#000' }} />
                                )}
                            </Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3, mb: 0.5 }}>
                                {ad.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.4 }}>
                                {ad.description.length > 60 ? `${ad.description.slice(0, 60)}...` : ad.description}
                            </Typography>
                            <Button
                                size="small"
                                endIcon={<ChevronRight sx={{ fontSize: 16 }} />}
                                sx={{
                                    p: 0,
                                    minWidth: 0,
                                    fontSize: '0.75rem',
                                    color: categoryColors[ad.category],
                                    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                                }}
                            >
                                {ad.ctaText}
                            </Button>
                        </Box>
                    </Card>
                ))}
                
            </Box>
            <AdDrawer open={drawerOpen} onClose={handleDrawerClose} ad={selectedAd} page={page} placement="sidebar" />
        </>
    );
};

export default SidebarAd;
